import type {IGeocode} from "@mytour/shared";
import axios from "axios";

/**
 * Coalescence anti double-appel (#1124) : deux géocodages identiques dans une
 * fenêtre courte partagent le même appel réseau — voir docs/context/geocodage.md
 * § Arbitrages encore ouverts pour le détail des compromis (TTL, résidu #1124-F1).
 */
const CACHE_TTL_MS = 8_000;

interface CacheEntry {
  promise: Promise<IGeocode>;
  resolvedAt: number | null;
}

const cache = new Map<string, CacheEntry>();

function cacheKey(zip: string, city: string, address: string, address2?: string): string {
  return JSON.stringify([zip, city, address, address2 ?? ""]);
}

function sweepExpired(now: number): void {
  for (const [key, entry] of cache) {
    if (entry.resolvedAt !== null && now - entry.resolvedAt >= CACHE_TTL_MS) {
      cache.delete(key);
    }
  }
}

export const getGeocode = async (
  zip: string,
  city: string,
  address: string,
  address2?: string
): Promise<IGeocode> => {
  const now = Date.now();
  sweepExpired(now);

  const key = cacheKey(zip, city, address, address2);
  const cached = cache.get(key);
  if (cached && (cached.resolvedAt === null || now - cached.resolvedAt < CACHE_TTL_MS)) {
    return cached.promise;
  }

  const promise = axios
    .post<IGeocode>("api/custom/geocode", {zip, city, address, address2})
    .then((response) => response.data);
  const entry: CacheEntry = {promise, resolvedAt: null};
  cache.set(key, entry);
  promise.then(
    () => {
      if (cache.get(key) === entry) {
        entry.resolvedAt = Date.now();
      }
    },
    () => {
      if (cache.get(key) === entry) {
        cache.delete(key);
      }
    }
  );
  return promise;
};
